import { dateFormatter } from "project-config/utils/date-formatter";
import {
  InvoicingStatus,
  InvoiceTypeEnum,
  InvoicingStatusColor,
} from "../constants";
import { useGlobalStore } from "@/store/GlobalStore";
import { Zone } from "project-config/constants/user-info";
import RejectIcon from "@/assets/icons/invoice/reject";
import CopyIcon from "@/assets/icons/invoice/copy";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, Tooltip, Popconfirm } from "@renderbus/ui";
import { useInvoiceStore } from "../store/invoice-store";
import { useInitRecordInfo } from "./useInitRecordInfo";
import { useInitInvoiceDetail } from "./useInitInvoiceDetail";

interface InvoiceStatusProps {
  statusColor?: string;
}
const InvoiceStatus = styled.div<InvoiceStatusProps>`
  position: relative;
  padding-left: 18px;
  display: flex;
  &:before {
    content: "";
    position: absolute;
    border-radius: 50%;
    width: 6px;
    height: 6px;
    background: ${(props) => props.statusColor};
    left: 0;
    top: 10px;
  }
  svg {
    margin: 4px 5px 0;
  }
`;
const InvoiceTrackNumber = styled.span`
  font-size: 12px;
  color: #138eff;
  padding-left: 12px;
  line-height: 22px;
`;

const InvoiceRecordOperate = styled.div`
  display: flex;
  align-items: center;
  a {
    color: #ff4444;
    margin-left: 16px;
  }
  button {
    color: #13cb75 !important;
  }
`;

export function useGetInvoiceRecordTable() {
  const { t } = useTranslation("invoice");
  const { userInfo } = useGlobalStore();
  const { setCurrentinvoiceOrderNumber, setOpenDrawer } = useInvoiceStore();
  const { handleRevokeRecord } = useInitInvoiceDetail();
  const { fetchInvoiceRecord } = useInitRecordInfo();
  const handleOperateRevoke = async (invoiceOrderNumber: string) => {
    await handleRevokeRecord(invoiceOrderNumber);
    fetchInvoiceRecord(1);
  };
  const columns = [
    {
      title: t("invoice_order_list"),
      dataIndex: "invoiceOrderNumber",
      key: "invoiceOrderNumber",
    },
    {
      title: t("invoice_invoice_creatme_time"),
      dataIndex: "createDate",
      key: "createDate",
      render: (row: number) => dateFormatter(row),
    },
    {
      title: t("invoice_invoice_create_type"),
      dataIndex: "type",
      key: "type",
      render: (row: number) => t(`invoice_invoice_type_arr_${row}`),
      filters: [
        {
          text: t("invoice_invoice_type_arr1"),
          value: InvoiceTypeEnum.ElectronicVAT,
        },
        {
          text: t("invoice_invoice_type_arr2"),
          value: InvoiceTypeEnum.PaperVAT,
        },
      ],
    },
    {
      title: t("invoice_invoice_create_money"),
      dataIndex: "money",
      key: "money",
      render: (row: number) =>
        `${userInfo?.zone === Zone.Domestic ? "￥" : "$"}${row}`,
    },
    {
      title: t("invoice_invoice_state"),
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (_: any, row: any) => {
        return (
          <InvoiceStatus statusColor={InvoicingStatusColor.get(row.status)}>
            {t(`invoice_invoice_status_${row.status}`)}
            {row.status === InvoicingStatus.Billed &&
              row.type === InvoiceTypeEnum.PaperVAT && (
                <Tooltip
                  direction="bottomLeft"
                  title={
                    <span style={{ color: "#202124" }}>
                      {row.expressNumber}&nbsp;&nbsp;
                      <CopyIcon />
                    </span>
                  }
                  tooltipText={
                    <InvoiceTrackNumber>
                      {t("invoice_invoice_tracking_number")}
                    </InvoiceTrackNumber>
                  }
                />
              )}
            {row.status === InvoicingStatus.Rejected && (
              <Tooltip
                direction="bottomLeft"
                title={<span style={{ color: "#202124" }}>{row.remarks}</span>}
                tooltipText={
                  <div>
                    <RejectIcon />
                  </div>
                }
              />
            )}
          </InvoiceStatus>
        );
      },
      filters: [
        { text: t("invoice_invoice_status_0"), value: -1 },
        {
          text: t(`invoice_invoice_status_${InvoicingStatus.Billed}`),
          value: InvoicingStatus.Billed,
        },
        {
          text: t(`invoice_invoice_status_${InvoicingStatus.OutOfDate}`),
          value: InvoicingStatus.OutOfDate,
        },
        {
          text: t(`invoice_invoice_status_${InvoicingStatus.Rejected}`),
          value: InvoicingStatus.Rejected,
        },
      ],
    },
    {
      title: t("invoice_operate"),
      dataIndex: "operate",
      key: "operate",
      width: 140,
      render: (_: any, row: any) => {
        return (
          <InvoiceRecordOperate>
            <Button
              type="text"
              className="table-text-btn"
              onClick={() => {
                setCurrentinvoiceOrderNumber(row.invoiceOrderNumber);
                setOpenDrawer(true);
              }}
            >
              {t("invoice_operation_detail")}
            </Button>
            {row.status === InvoicingStatus.TobeBilled && (
              <Popconfirm
                placement="bottomLeft"
                title={t("invoice_revoke_tip")}
                popconfirmText={t("invoice_operate_revoke_button") || ""}
                onConfirm={() => handleOperateRevoke(row.invoiceOrderNumber)}
              ></Popconfirm>
            )}
          </InvoiceRecordOperate>
        );
      },
    },
  ];
  return columns;
}
